import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
   getExamQuestionById,
   getExamTicketById,
   getExamTicketQuestions,
   updateExamQuestion,
   loadAllPracticeTickets,
   buildExamTicketsExportBundle,
   downloadJsonExport,
   getPracticeTicketIdRange,
} from "../../api/examAdminService";
import {
   buildExamQuestionPutPayload,
   normalizeExamQuestionRecord,
   parseJsonArraySafe,
   toPositiveInt,
} from "../../utils/examQuestionFormat";
import {
   extractTicketNumber,
   resolvePracticeTicketNumber,
} from "../../utils/examTicketsOfficialImport";
import {
   applyRuPatchToQuestion,
   buildQuestionPreviewDiff,
   parseTheoryRuReorderPatch,
} from "../../utils/theoryRuReorderPatch";
import {
   buildTheoryOfficialApplicationPlan,
   isOfficialTheoryImport,
   parseTheoryOfficialImport,
   orderQuestionsByIds,
   resolvePracticeTicket,
   THEORY_TICKETS_MAX,
} from "../../utils/theoryOfficialTicketsImport";
import {
   applyAutoTestAbUpdates,
   AUTO_TEST_AB_RULE,
   isAutoTestAbImport,
   loadPracticeSiteSnapshot,
   parseAutoTestAbImport,
   runAutoTestAbCorrection,
} from "../../utils/autoTestAbImport";

const APPLY_DELAY_MS = 120;
const PREVIEW_CONCURRENCY = 8;

function safeList(value) {
   return Array.isArray(value) ? value : [];
}

function uniqPositiveInts(values = []) {
   const seen = new Set();
   const result = [];

   for (const value of values) {
      const id = toPositiveInt(value);
      if (!id || seen.has(id)) continue;
      seen.add(id);
      result.push(id);
   }

   return result;
}

function truncateText(value, max = 96) {
   const text = String(value ?? "").trim();
   if (text.length <= max) return text;
   return `${text.slice(0, max - 1)}…`;
}

function normalizePracticeTicketSummary(ticket, fallbackIndex = 0) {
   const id = toPositiveInt(ticket?.id);
   if (!id) return null;

   const ticketNo = resolvePracticeTicketNumber(ticket);
   const name =
      String(ticket?.name ?? "").trim() ||
      (ticketNo ? `P${ticketNo}` : `Bilet ${fallbackIndex + 1}`);
   const questionCount =
      toPositiveInt(
         ticket?._count?.questions ??
            ticket?.questionCount ??
            ticket?.questionsCount,
      ) ?? uniqPositiveInts(ticket?.questionIds).length;

   return {
      id,
      localId: `practice-ticket-${id}`,
      name,
      ticketNo,
      ticketCode: ticketNo ? `P${ticketNo}` : "",
      questionCount,
      type: String(ticket?.type ?? "PRACTICE").trim() || "PRACTICE",
   };
}

function normalizePracticeTicketList(items) {
   const range = getPracticeTicketIdRange();

   return safeList(items)
      .map((item, index) => normalizePracticeTicketSummary(item, index))
      .filter(
         (ticket) =>
            ticket &&
            Number(ticket.id) >= range.start &&
            Number(ticket.id) <= range.end,
      )
      .sort(
         (left, right) =>
            Number(left.ticketNo || 999) - Number(right.ticketNo || 999) ||
            Number(left.id) - Number(right.id),
      );
}

function readQuestionIdsFromTicket(raw, fallbackQuestions = []) {
   const source = raw?.data && typeof raw.data === "object" ? raw.data : raw;
   const directIds = uniqPositiveInts(
      parseJsonArraySafe(source?.questionIds, safeList(source?.questionIds)),
   );
   if (directIds.length) return directIds;

   return uniqPositiveInts(
      safeList(fallbackQuestions).map(
         (question) => question?.id ?? question?.questionId,
      ),
   );
}

const DECISION = {
   PENDING: "pending",
   APPLIED: "applied",
   SKIPPED: "skipped",
   ERROR: "error",
};

function sleep(ms) {
   return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function confidenceClass(value) {
   const text = String(value ?? "").toLowerCase();
   if (text.includes("low")) return "low";
   if (text.includes("medium")) return "medium";
   if (text.includes("high") || text.includes("reviewed")) return "high";
   return "neutral";
}

function decisionLabel(status) {
   if (status === DECISION.APPLIED) return "Schimbat";
   if (status === DECISION.SKIPPED) return "Păstrat";
   if (status === DECISION.ERROR) return "Eroare";
   return "De decis";
}

function patchTypeLabel(type) {
   if (type === "auto_test_AB" || String(type || "").includes("auto_test")) {
      return "auto_test_AB oficial";
   }
   if (type === "official_practice_tickets_ro_ru_correct_answers") {
      return "bilete oficiale P1–P24";
   }
   if (type?.includes?.("official")) {
      return "import oficial RO/RU";
   }
   if (type === "full_ro_ru_answer_text_repair") {
      return "corecție text RO + RU";
   }
   if (type === "ro_ru_answer_reorder_patch") {
      return "reordonare RU";
   }
   return type || "patch teorie";
}

function applySuccessMessage(patch) {
   if (patch?.patchKind === "text_repair") {
      return "RO și RU actualizate (PUT). Ordinea răspunsurilor neschimbată.";
   }
   return "RU actualizat (PUT). RO neschimbat.";
}

function isReviewableItem(item) {
   return Boolean(
      !item?.loadError && !item?.diff?.error && item?.diff?.hasChanges,
   );
}

function TheoryPreviewCard({ item, decisionStatus }) {
   const { patch, diff, loadError } = item;
   const before = diff?.before;
   const after = diff?.after;
   const changedRuSet = new Set(diff?.changedAnswerIndexes || []);
   const changedRoSet = new Set(diff?.changedRoAnswerIndexes || []);

   return (
      <article
         className={`theory-correction__card theory-correction__card--${decisionStatus}`}
      >
         <header className="theory-correction__card-head">
            <div className="theory-correction__card-meta">
               <strong>
                  {patch.ticketCode || "—"} · {patch.bKey || "—"} · #
                  {patch.questionId}
               </strong>
               {patch.short ? (
                  <span className="theory-correction__card-short">{patch.short}</span>
               ) : null}
            </div>
            <div className="theory-correction__card-badges">
               <span
                  className={`theory-correction__decision-badge theory-correction__decision-badge--${decisionStatus}`}
               >
                  {decisionLabel(decisionStatus)}
               </span>
               {patch.confidence ? (
                  <span
                     className={`theory-correction__confidence theory-correction__confidence--${confidenceClass(patch.confidence)}`}
                  >
                     {patch.confidence}
                  </span>
               ) : null}
            </div>
         </header>

         {loadError ? (
            <p className="theory-correction__card-error">{loadError}</p>
         ) : null}

         {!loadError && before ? (
            <>
               <div className="theory-correction__question-frame">
                  <div className="theory-correction__question-line">
                     <span className="theory-correction__lang-tag">RO</span>
                     <p>{before.textRo || "—"}</p>
                  </div>
                  <div className="theory-correction__question-line">
                     <span className="theory-correction__lang-tag">RU</span>
                     <p>{before.textRu || "—"}</p>
                  </div>
               </div>

               <div className="theory-correction__compare-grid">
                  <div className="theory-correction__compare-cell theory-correction__compare-cell--before">
                     <h4>Acum · Română</h4>
                     <ul>
                        {before.answers.map((answer, index) => (
                           <li key={`before-ro-${index}`}>
                              <span className="theory-correction__option-index">
                                 {index + 1}
                              </span>
                              {answer.ro || "—"}
                           </li>
                        ))}
                     </ul>
                  </div>

                  <div className="theory-correction__compare-cell theory-correction__compare-cell--before">
                     <h4>Acum · Rusă</h4>
                     <ul>
                        {before.answers.map((answer, index) => (
                           <li key={`before-ru-${index}`}>
                              <span className="theory-correction__option-index">
                                 {index + 1}
                              </span>
                              {answer.ru || "—"}
                           </li>
                        ))}
                     </ul>
                  </div>

                  <div className="theory-correction__compare-cell theory-correction__compare-cell--after">
                     <h4>După · Română</h4>
                     <ul>
                        {(after?.answers || before.answers).map((answer, index) => (
                           <li
                              key={`after-ro-${index}`}
                              className={changedRoSet.has(index) ? "is-changed" : ""}
                           >
                              <span className="theory-correction__option-index">
                                 {index + 1}
                              </span>
                              {answer.ro || "—"}
                           </li>
                        ))}
                     </ul>
                  </div>

                  <div className="theory-correction__compare-cell theory-correction__compare-cell--after">
                     <h4>După · Rusă</h4>
                     {diff?.error ? (
                        <p className="theory-correction__card-error">{diff.error}</p>
                     ) : (
                        <ul>
                           {(after?.answers || []).map((answer, index) => (
                              <li
                                 key={`after-ru-${index}`}
                                 className={changedRuSet.has(index) ? "is-changed" : ""}
                              >
                                 <span className="theory-correction__option-index">
                                    {index + 1}
                                 </span>
                                 {answer.ru || "—"}
                              </li>
                           ))}
                        </ul>
                     )}
                  </div>
               </div>
            </>
         ) : null}
      </article>
   );
}

async function mapWithConcurrency(items, limit, mapper) {
   const results = new Array(items.length);
   let cursor = 0;

   async function worker() {
      while (cursor < items.length) {
         const index = cursor;
         cursor += 1;
         results[index] = await mapper(items[index], index);
      }
   }

   const workers = Array.from(
      { length: Math.min(limit, items.length) },
      () => worker(),
   );
   await Promise.all(workers);
   return results;
}

async function applyQuestionPatch(item) {
   const raw = await getExamQuestionById(item.patch.questionId);
   const current = normalizeExamQuestionRecord(raw);
   if (!current) {
      throw new Error("Întrebarea nu a putut fi normalizată.");
   }

   const nextDraft = applyRuPatchToQuestion(current, item.patch);
   const payload = buildExamQuestionPutPayload(nextDraft);
   await updateExamQuestion(item.patch.questionId, payload);
}

export default function TheoryCorrectionAdmin() {
   const [alert, setAlert] = useState(null);
   const [patchFileName, setPatchFileName] = useState("");
   const [parsedPatch, setParsedPatch] = useState(null);
   const [previewItems, setPreviewItems] = useState([]);
   const [previewLoading, setPreviewLoading] = useState(false);
   const [previewProgress, setPreviewProgress] = useState(null);
   const [selectedTicket, setSelectedTicket] = useState("all");
   const [currentIndex, setCurrentIndex] = useState(0);
   const [decisions, setDecisions] = useState({});
   const [itemBusy, setItemBusy] = useState(false);
   const [bulkApplying, setBulkApplying] = useState(false);
   const [applyProgress, setApplyProgress] = useState(null);
   const [applyResults, setApplyResults] = useState([]);
   const [exportLoading, setExportLoading] = useState(false);
   const [exportProgress, setExportProgress] = useState(null);
   const [importMode, setImportMode] = useState(null);
   const [officialBundle, setOfficialBundle] = useState(null);
   const [officialApplying, setOfficialApplying] = useState(false);
   const [officialApplyProgress, setOfficialApplyProgress] = useState(null);
   const [autoTestAbBundle, setAutoTestAbBundle] = useState(null);
   const [autoTestAbReport, setAutoTestAbReport] = useState(null);
   const [autoTestAbDryRunLoading, setAutoTestAbDryRunLoading] = useState(false);
   const [autoTestAbApplying, setAutoTestAbApplying] = useState(false);
   const [autoTestAbApplyProgress, setAutoTestAbApplyProgress] = useState(null);
   const siteSnapshotRef = useRef([]);
   const abortRef = useRef(false);
   const browseTicketRowsRef = useRef([]);
   const browseTicketLoadRef = useRef(0);

   const [browseTicketRows, setBrowseTicketRows] = useState([]);
   const [browseTicketLoading, setBrowseTicketLoading] = useState(false);
   const [browseTicketSearch, setBrowseTicketSearch] = useState("");
   const [browseSelectedTicketId, setBrowseSelectedTicketId] = useState(null);
   const [browseSelectedTicketLoading, setBrowseSelectedTicketLoading] =
      useState(false);
   const [browseSelectedTicketMeta, setBrowseSelectedTicketMeta] = useState(null);
   const [browseQuestionRows, setBrowseQuestionRows] = useState([]);
   const [browseSelectedQuestionId, setBrowseSelectedQuestionId] = useState(null);
   const [browsePreviewLang, setBrowsePreviewLang] = useState("ro");

   const pushAlert = useCallback((message, type = "info") => {
      setAlert({ message, type });
   }, []);

   useEffect(() => {
      browseTicketRowsRef.current = browseTicketRows;
   }, [browseTicketRows]);

   const loadBrowseTickets = useCallback(async () => {
      try {
         setBrowseTicketLoading(true);
         const rows = normalizePracticeTicketList(await loadAllPracticeTickets());
         setBrowseTicketRows(rows);

         if (!rows.length) {
            setBrowseSelectedTicketId(null);
            return;
         }

         setBrowseSelectedTicketId((current) => {
            const exists = rows.some((row) => Number(row.id) === Number(current));
            if (exists) return current;
            return rows[0].id;
         });
      } catch (error) {
         pushAlert(
            `Nu am putut încărca biletele de teorie: ${error?.message || "eroare"}`,
            "error",
         );
      } finally {
         setBrowseTicketLoading(false);
      }
   }, [pushAlert]);

   const loadBrowseSelectedTicket = useCallback(async (ticketId) => {
      const resolvedId = toPositiveInt(ticketId);
      if (!resolvedId) {
         setBrowseSelectedTicketMeta(null);
         setBrowseQuestionRows([]);
         setBrowseSelectedQuestionId(null);
         return;
      }

      const requestId = browseTicketLoadRef.current + 1;
      browseTicketLoadRef.current = requestId;

      try {
         setBrowseSelectedTicketLoading(true);

         const fallbackSummary =
            browseTicketRowsRef.current.find(
               (ticket) => Number(ticket.id) === Number(resolvedId),
            ) || null;

         const [ticketRaw, questionsRaw] = await Promise.all([
            getExamTicketById(resolvedId),
            getExamTicketQuestions(resolvedId),
         ]);

         if (browseTicketLoadRef.current !== requestId) return;

         const normalizedQuestions = safeList(questionsRaw?.items)
            .map((question, index) => normalizeExamQuestionRecord(question, index))
            .filter(Boolean);

         const questionIds = readQuestionIdsFromTicket(
            ticketRaw,
            normalizedQuestions,
         );
         const orderedQuestions = orderQuestionsByIds(
            normalizedQuestions,
            questionIds,
         ).map((question, index) => ({
            ...question,
            positionInTicket: index + 1,
         }));

         const ticketNo =
            fallbackSummary?.ticketNo || resolvePracticeTicketNumber(ticketRaw);
         const ticketCode = ticketNo ? `P${ticketNo}` : fallbackSummary?.ticketCode || "";

         setBrowseSelectedTicketMeta({
            id: resolvedId,
            name: String(ticketRaw?.name ?? fallbackSummary?.name ?? "").trim(),
            ticketNo,
            ticketCode,
            questionIds: orderedQuestions.map((question) => question.id),
            questionCount: orderedQuestions.length,
            type: String(ticketRaw?.type ?? fallbackSummary?.type ?? "PRACTICE"),
         });
         setBrowseQuestionRows(orderedQuestions);
         setBrowseSelectedQuestionId((current) => {
            const hasCurrent = orderedQuestions.some(
               (question) => Number(question.id) === Number(current),
            );
            return hasCurrent ? current : orderedQuestions[0]?.id ?? null;
         });
      } catch (error) {
         if (browseTicketLoadRef.current !== requestId) return;
         pushAlert(
            `Nu am putut încărca întrebările biletului: ${error?.message || "eroare"}`,
            "error",
         );
         setBrowseSelectedTicketMeta(null);
         setBrowseQuestionRows([]);
         setBrowseSelectedQuestionId(null);
      } finally {
         if (browseTicketLoadRef.current === requestId) {
            setBrowseSelectedTicketLoading(false);
         }
      }
   }, [pushAlert]);

   useEffect(() => {
      loadBrowseTickets();
   }, [loadBrowseTickets]);

   useEffect(() => {
      if (!browseSelectedTicketId) return;
      loadBrowseSelectedTicket(browseSelectedTicketId);
   }, [browseSelectedTicketId, loadBrowseSelectedTicket]);

   const getDecision = useCallback(
      (item) => {
         const id = item?.patch?.questionId;
         if (!id) return DECISION.PENDING;
         if (decisions[id]) return decisions[id];
         if (item.loadError || item.diff?.error) return DECISION.ERROR;
         if (!item.diff?.hasChanges) return DECISION.SKIPPED;
         return DECISION.PENDING;
      },
      [decisions],
   );

   const loadPreview = useCallback(
      async (parsed) => {
         const patches = parsed?.questionPatches || [];
         if (!patches.length) {
            setPreviewItems([]);
            return;
         }

         abortRef.current = false;
         setPreviewLoading(true);
         setPreviewProgress({ current: 0, total: patches.length });
         setPreviewItems([]);
         setApplyResults([]);
         setDecisions({});
         setCurrentIndex(0);

         let completed = 0;

         try {
            const rows = await mapWithConcurrency(
               patches,
               PREVIEW_CONCURRENCY,
               async (patch) => {
                  if (abortRef.current) {
                     return { patch, diff: null, loadError: "Anulat." };
                  }

                  try {
                     const raw = await getExamQuestionById(patch.questionId);
                     const current = normalizeExamQuestionRecord(raw);
                     if (!current) {
                        throw new Error("Întrebarea nu a putut fi normalizată.");
                     }

                     const diff = buildQuestionPreviewDiff(current, patch);
                     completed += 1;
                     setPreviewProgress({
                        current: completed,
                        total: patches.length,
                     });

                     return { patch, diff, loadError: null };
                  } catch (error) {
                     completed += 1;
                     setPreviewProgress({
                        current: completed,
                        total: patches.length,
                     });

                     return {
                        patch,
                        diff: null,
                        loadError: error?.message || "Eroare la încărcare.",
                     };
                  }
               },
            );

            if (!abortRef.current) {
               setPreviewItems(rows);
               const ready = rows.filter(isReviewableItem).length;
               pushAlert(
                  `Previzualizare gata: ${ready} întrebări de revizuit din ${rows.length}.`,
                  "success",
               );
            }
         } catch (error) {
            pushAlert(
               error?.message || "Nu am putut genera previzualizarea.",
               "error",
            );
         } finally {
            setPreviewLoading(false);
            setPreviewProgress(null);
         }
      },
      [pushAlert],
   );

   const loadSiteSnapshot = useCallback(async () => {
      const entries = await loadPracticeSiteSnapshot({
         loadTickets: loadAllPracticeTickets,
         loadTicketById: getExamTicketById,
         loadTicketQuestions: getExamTicketQuestions,
         maxTickets: THEORY_TICKETS_MAX,
      });
      siteSnapshotRef.current = entries;
      return entries;
   }, []);

   const runAutoTestAbDryRun = useCallback(
      async (bundle) => {
         setAutoTestAbDryRunLoading(true);
         setAutoTestAbReport(null);

         try {
            const siteEntries = await loadSiteSnapshot();
            if (!siteEntries.length) {
               throw new Error(
                  "Nu am găsit întrebări pe site pentru P1–P24. Verifică biletele Practice (#246–#269).",
               );
            }
            const result = runAutoTestAbCorrection({
               bundle,
               siteEntries,
               apply: false,
            });
            setAutoTestAbReport(result);

            const { report } = result;
            pushAlert(
               `Dry-run: ${report.foundInSite}/${report.officialTotal} găsite, ${report.wouldUpdate} de actualizat, ${report.missingInSite} lipsă în site, ${report.notInOfficialFile} doar în site.`,
               report.missingInSite || report.notInOfficialFile ? "warning" : "success",
            );
         } catch (error) {
            setAutoTestAbReport(null);
            pushAlert(
               error?.message || "Nu am putut rula dry-run pentru auto_test_AB.",
               "error",
            );
         } finally {
            setAutoTestAbDryRunLoading(false);
         }
      },
      [loadSiteSnapshot, pushAlert],
   );

   const handleImportFile = useCallback(
      async (event) => {
         const file = event.target.files?.[0];
         event.target.value = "";
         if (!file) return;

         try {
            const text = String(await file.text()).replace(/^\uFEFF/, "");
            const json = JSON.parse(text);

            setPatchFileName(file.name);
            setPreviewItems([]);
            setApplyResults([]);
            setSelectedTicket("all");
            setCurrentIndex(0);
            setDecisions({});
            setOfficialBundle(null);
            setAutoTestAbBundle(null);
            setAutoTestAbReport(null);
            setParsedPatch(null);

            if (isAutoTestAbImport(json)) {
               const bundle = parseAutoTestAbImport(json);
               if (!bundle.items.length) {
                  throw new Error(
                     bundle.errors[0] ||
                        "Fișierul auto_test_AB nu conține întrebări valide.",
                  );
               }

               setImportMode("autoTestAb");
               setAutoTestAbBundle(bundle);
               pushAlert(
                  `Import auto_test_AB: ${bundle.summary.officialTotal} întrebări, ${bundle.summary.ticketsTotal} bilete. Se rulează dry-run…`,
                  bundle.errors.length ? "warning" : "info",
               );
               await runAutoTestAbDryRun(bundle);
               return;
            }

            if (isOfficialTheoryImport(json)) {
               const official = parseTheoryOfficialImport(json);
               if (!official.tickets.length) {
                  throw new Error(
                     official.errors[0] ||
                        "Fișierul oficial nu conține bilete aplicabile.",
                  );
               }

               setImportMode("official");
               setOfficialBundle(official);

               const reviewNote =
                  official.summary.reviewCount > 0
                     ? ` · ${official.summary.reviewCount} de verificat`
                     : "";

               pushAlert(
                  `Import oficial: ${official.summary.ticketsTotal} bilete (${official.summary.ticketRange}), ${official.summary.questionsTotal} întrebări${reviewNote}.`,
                  official.errors.length || official.summary.reviewCount
                     ? "warning"
                     : "success",
               );
               return;
            }

            const parsed = parseTheoryRuReorderPatch(json);
            setImportMode("patch");
            setParsedPatch(parsed);

            if (!parsed.questionPatches.length) {
               pushAlert(
                  parsed.errors[0] ||
                     "Fișierul nu conține corecții aplicabile.",
                  "error",
               );
               return;
            }

            if (parsed.errors.length) {
               pushAlert(
                  `Import: ${parsed.questionPatches.length} corecții, ${parsed.errors.length} avertismente.`,
                  "warning",
               );
            } else {
               pushAlert(
                  `Import reușit: ${parsed.questionPatches.length} corecții (${patchTypeLabel(parsed.type)}).`,
                  "success",
               );
            }

            await loadPreview(parsed);
         } catch (error) {
            setPatchFileName("");
            setParsedPatch(null);
            setOfficialBundle(null);
            setAutoTestAbBundle(null);
            setAutoTestAbReport(null);
            setImportMode(null);
            setPreviewItems([]);
            pushAlert(
               error?.message || "Fișier JSON invalid sau structură necunoscută.",
               "error",
            );
         }
      },
      [loadPreview, pushAlert, runAutoTestAbDryRun],
   );

   const handleApplyAutoTestAbImport = useCallback(async () => {
      if (!autoTestAbBundle?.items?.length || autoTestAbApplying) return;
      if (!autoTestAbReport?.updates?.length) {
         pushAlert("Dry-run: nu există modificări de aplicat.", "warning");
         return;
      }

      const ruleText = autoTestAbBundle.rule || AUTO_TEST_AB_RULE;
      const { report } = autoTestAbReport;

      const ok = window.confirm(
         `REGULI auto_test_AB (P1–P${THEORY_TICKETS_MAX}):\n\n${ruleText}\n\n---\n\n` +
            `Găsite: ${report.foundInSite}\n` +
            `De actualizat: ${report.wouldUpdate || report.updated}\n` +
            `Lipsă în site: ${report.missingInSite}\n` +
            `Doar în site: ${report.notInOfficialFile}\n\n` +
            `NU se creează întrebări noi. Imaginile rămân ale site-ului.\n\nContinui cu APPLY?`,
      );
      if (!ok) return;

      setAutoTestAbApplying(true);
      setAutoTestAbApplyProgress({
         current: 0,
         total: autoTestAbReport.updates.length,
      });

      try {
         const siteEntries =
            siteSnapshotRef.current.length > 0
               ? siteSnapshotRef.current
               : await loadSiteSnapshot();

         const planned = runAutoTestAbCorrection({
            bundle: autoTestAbBundle,
            siteEntries,
            apply: false,
         });

         const log = await applyAutoTestAbUpdates(
            planned.updates,
            updateExamQuestion,
            {
               onProgress: setAutoTestAbApplyProgress,
            },
         );

         const applied = log.filter((row) => row.status === "ok").length;
         const failed = log.filter((row) => row.status === "error").length;

         const finalReport = runAutoTestAbCorrection({
            bundle: autoTestAbBundle,
            siteEntries,
            apply: true,
         });
         finalReport.report.updated = applied;
         setAutoTestAbReport(finalReport);
         setApplyResults(log);

         if (failed) {
            pushAlert(
               `Apply finalizat cu erori: ${applied} reușite, ${failed} eșuate.`,
               "warning",
            );
         } else {
            pushAlert(`Apply reușit: ${applied} întrebări actualizate.`, "success");
         }
      } catch (error) {
         pushAlert(
            error?.message || "Nu am putut aplica importul auto_test_AB.",
            "error",
         );
      } finally {
         setAutoTestAbApplying(false);
         setAutoTestAbApplyProgress(null);
      }
   }, [
      autoTestAbApplying,
      autoTestAbBundle,
      autoTestAbReport,
      loadSiteSnapshot,
      pushAlert,
   ]);

   const handleApplyOfficialImport = useCallback(async () => {
      if (!officialBundle?.tickets?.length || officialApplying) return;

      const ruleText =
         officialBundle.rule ||
         "Ordinea întrebărilor și a răspunsurilor este din documentul oficial RO. Rusa este aliniată după română. Se actualizează textele și răspunsul corect.";

      const ok = window.confirm(
         `REGULI IMPORT (P1–P${THEORY_TICKETS_MAX}):\n\n${ruleText}\n\n---\n\nSe vor modifica ${officialBundle.summary.questionsTotal} întrebări din ${officialBundle.summary.ticketsTotal} bilete.\n\nOrdinea din fișier este păstrată. Continui?`,
      );
      if (!ok) return;

      setOfficialApplying(true);
      setOfficialApplyProgress({ current: 0, total: officialBundle.tickets.length });

      const log = [];
      let appliedQuestions = 0;

      try {
         const dbTickets = await loadAllPracticeTickets();
         if (!dbTickets.length) {
            throw new Error("Nu am găsit biletele de teorie P1–P24 în backend.");
         }

         let ticketStep = 0;
         for (const importTicket of officialBundle.tickets) {
            ticketStep += 1;
            setOfficialApplyProgress({
               current: ticketStep,
               total: officialBundle.tickets.length,
               label: importTicket.ticketCode,
            });

            const dbTicket = resolvePracticeTicket(importTicket, dbTickets);
            if (!dbTicket?.id) {
               log.push({
                  status: "error",
                  target: importTicket.ticketCode,
                  message: "Biletul nu a fost găsit în backend.",
               });
               continue;
            }

            const ticketRaw = await getExamTicketById(dbTicket.id);
            const questionsResponse = await getExamTicketQuestions(dbTicket.id);
            const existingQuestions = safeList(questionsResponse?.items)
               .map((row, index) => normalizeExamQuestionRecord(row, index))
               .filter(Boolean);

            const questionIds =
               safeList(ticketRaw?.questionIds) ||
               safeList(ticketRaw?.data?.questionIds) ||
               existingQuestions.map((question) => question.id);

            const plan = buildTheoryOfficialApplicationPlan(
               importTicket,
               existingQuestions,
               questionIds,
            );

            let ticketApplied = 0;
            for (const update of plan.updates) {
               if (update.status !== "ready") continue;
               await updateExamQuestion(update.questionId, update.payload);
               ticketApplied += 1;
            }

            appliedQuestions += ticketApplied;

            if (plan.missingCount) {
               log.push({
                  status: "warning",
                  target: `${importTicket.ticketCode} · #${dbTicket.id}`,
                  message: `${ticketApplied} actualizate, ${plan.missingCount} lipsă.`,
               });
            } else {
               log.push({
                  status: "ok",
                  target: `${importTicket.ticketCode} · #${dbTicket.id}`,
                  message: `${ticketApplied} întrebări actualizate.`,
               });
            }
         }

         setApplyResults(log);
         const failed = log.filter((row) => row.status === "error").length;
         if (failed) {
            pushAlert(
               `Import parțial: ${appliedQuestions} întrebări, ${failed} bilete negăsite.`,
               "warning",
            );
         } else {
            pushAlert(
               `Import aplicat: ${appliedQuestions} întrebări actualizate.`,
               "success",
            );
         }
      } catch (error) {
         pushAlert(
            error?.message || "Nu am putut aplica importul oficial.",
            "error",
         );
      } finally {
         setOfficialApplying(false);
         setOfficialApplyProgress(null);
      }
   }, [officialApplying, officialBundle, pushAlert]);

   const handleExportAllTickets = useCallback(async () => {
      if (exportLoading || previewLoading || bulkApplying || itemBusy) return;

      const { start, end } = getPracticeTicketIdRange();

      setExportLoading(true);
      setExportProgress({ current: 0, total: 0, ticketName: "Pornire…" });

      try {
         const bundle = await buildExamTicketsExportBundle({
            ticketType: "PRACTICE",
            onProgress: (progress) => setExportProgress(progress),
         });

         const stamp = new Date().toISOString().slice(0, 10);
         downloadJsonExport(
            bundle,
            `bilete-teorie-P1-P24-${start}-${end}-${stamp}.json`,
         );

         pushAlert(
            `Export reușit: ${bundle.summary.ticketsTotal} bilete (id #${start}–#${end}), ${bundle.summary.questionsTotal} întrebări.`,
            "success",
         );
      } catch (error) {
         pushAlert(
            error?.message || "Nu am putut exporta biletele.",
            "error",
         );
      } finally {
         setExportLoading(false);
         setExportProgress(null);
      }
   }, [bulkApplying, exportLoading, itemBusy, previewLoading, pushAlert]);

   const ticketTabs = useMemo(() => {
      const codes = new Set();
      for (const item of previewItems) {
         if (item?.patch?.ticketCode) codes.add(item.patch.ticketCode);
      }
      return [
         "all",
         ...Array.from(codes).sort((a, b) => {
            const na = Number(String(a).replace(/^P/i, ""));
            const nb = Number(String(b).replace(/^P/i, ""));
            if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
            return String(a).localeCompare(String(b));
         }),
      ];
   }, [previewItems]);

   const visiblePreviewItems = useMemo(() => {
      if (selectedTicket === "all") return previewItems;
      return previewItems.filter(
         (item) => item?.patch?.ticketCode === selectedTicket,
      );
   }, [previewItems, selectedTicket]);

   useEffect(() => {
      setCurrentIndex(0);
   }, [selectedTicket]);

   useEffect(() => {
      if (currentIndex >= visiblePreviewItems.length && visiblePreviewItems.length) {
         setCurrentIndex(Math.max(0, visiblePreviewItems.length - 1));
      }
   }, [currentIndex, visiblePreviewItems.length]);

   const currentItem = visiblePreviewItems[currentIndex] || null;
   const currentDecision = currentItem ? getDecision(currentItem) : DECISION.PENDING;
   const currentReviewable = currentItem ? isReviewableItem(currentItem) : false;

   const reviewStats = useMemo(() => {
      let pending = 0;
      let applied = 0;
      let skipped = 0;
      let errors = 0;

      for (const item of previewItems) {
         const status = getDecision(item);
         if (status === DECISION.APPLIED) applied += 1;
         else if (status === DECISION.SKIPPED) skipped += 1;
         else if (status === DECISION.ERROR) errors += 1;
         else if (isReviewableItem(item)) pending += 1;
      }

      return { pending, applied, skipped, errors, total: previewItems.length };
   }, [getDecision, previewItems]);

   const pendingItems = useMemo(
      () =>
         visiblePreviewItems.filter(
            (item) => isReviewableItem(item) && getDecision(item) === DECISION.PENDING,
         ),
      [getDecision, visiblePreviewItems],
   );

   const goToNextPending = useCallback(() => {
      if (!visiblePreviewItems.length) return;

      for (let offset = 1; offset <= visiblePreviewItems.length; offset += 1) {
         const index = (currentIndex + offset) % visiblePreviewItems.length;
         const item = visiblePreviewItems[index];
         if (isReviewableItem(item) && getDecision(item) === DECISION.PENDING) {
            setCurrentIndex(index);
            return;
         }
      }

      if (currentIndex < visiblePreviewItems.length - 1) {
         setCurrentIndex((value) => value + 1);
      }
   }, [currentIndex, getDecision, visiblePreviewItems]);

   const markDecision = useCallback((questionId, status) => {
      setDecisions((prev) => ({
         ...prev,
         [questionId]: status,
      }));
   }, []);

   const handleSkipCurrent = useCallback(() => {
      if (!currentItem) return;
      markDecision(currentItem.patch.questionId, DECISION.SKIPPED);
      goToNextPending();
   }, [currentItem, goToNextPending, markDecision]);

   const handleApplyCurrent = useCallback(async () => {
      if (!currentItem || !currentReviewable || itemBusy || bulkApplying) return;

      setItemBusy(true);
      try {
         await applyQuestionPatch(currentItem);
         markDecision(currentItem.patch.questionId, DECISION.APPLIED);
         setApplyResults((prev) => [
            {
               status: "ok",
               target: `${currentItem.patch.ticketCode || "Q"} · #${currentItem.patch.questionId}`,
               message: applySuccessMessage(currentItem.patch),
            },
            ...prev,
         ]);
         goToNextPending();
      } catch (error) {
         markDecision(currentItem.patch.questionId, DECISION.ERROR);
         pushAlert(
            error?.message || "Nu am putut aplica modificarea pentru această întrebare.",
            "error",
         );
      } finally {
         setItemBusy(false);
      }
   }, [
      bulkApplying,
      currentItem,
      currentReviewable,
      goToNextPending,
      itemBusy,
      markDecision,
      pushAlert,
   ]);

   const confirmAndApplyAll = useCallback(async () => {
      if (!pendingItems.length || bulkApplying || itemBusy) {
         pushAlert("Nu mai sunt întrebări în așteptare.", "warning");
         return;
      }

      const isTextRepair = pendingItems.some(
         (item) => item?.patch?.patchKind === "text_repair",
      );
      const ok = window.confirm(
         isTextRepair
            ? `Confirmi aplicarea pentru toate cele ${pendingItems.length} întrebări rămase?\n\nSe actualizează textele RO și RU; ordinea răspunsurilor rămâne neschimbată.`
            : `Confirmi aplicarea pentru toate cele ${pendingItems.length} întrebări rămase?\n\nRomână rămâne neschimbat.`,
      );
      if (!ok) return;

      abortRef.current = false;
      setBulkApplying(true);
      setApplyResults([]);

      const log = [];
      let step = 0;

      setApplyProgress({
         current: 0,
         total: pendingItems.length,
         label: "Pornire…",
      });

      try {
         for (const item of pendingItems) {
            if (abortRef.current) break;
            step += 1;
            const patch = item.patch;

            setApplyProgress({
               current: step,
               total: pendingItems.length,
               label: `${patch.ticketCode || "Q"} · #${patch.questionId}`,
            });

            try {
               await applyQuestionPatch(item);
               markDecision(patch.questionId, DECISION.APPLIED);
               log.push({
                  status: "ok",
                  target: `${patch.ticketCode || "Q"} · #${patch.questionId}`,
                  message: applySuccessMessage(patch),
               });
            } catch (error) {
               markDecision(patch.questionId, DECISION.ERROR);
               log.push({
                  status: "error",
                  target: `${patch.ticketCode || "Q"} · #${patch.questionId}`,
                  message: error?.message || "Eroare necunoscută.",
               });
            }

            await sleep(APPLY_DELAY_MS);
         }

         setApplyResults(log);
         const failed = log.filter((row) => row.status === "error").length;
         const applied = log.filter((row) => row.status === "ok").length;

         if (failed) {
            pushAlert(
               `Finalizat cu erori: ${applied} reușite, ${failed} eșuate.`,
               "warning",
            );
         } else {
            pushAlert(`Modificări aplicate: ${applied} întrebări actualizate.`, "success");
         }
      } catch (error) {
         pushAlert(error?.message || "Aplicarea s-a oprit cu eroare.", "error");
      } finally {
         setBulkApplying(false);
         setApplyProgress(null);
      }
   }, [bulkApplying, itemBusy, markDecision, pendingItems, pushAlert]);

   const stopRun = useCallback(() => {
      abortRef.current = true;
   }, []);

   const canGoPrev = currentIndex > 0;
   const canGoNext = currentIndex < visiblePreviewItems.length - 1;
   const busy =
      itemBusy ||
      bulkApplying ||
      exportLoading ||
      officialApplying ||
      autoTestAbDryRunLoading ||
      autoTestAbApplying;

   const practiceIdRange = useMemo(() => getPracticeTicketIdRange(), []);

   const filteredBrowseTickets = useMemo(() => {
      const query = String(browseTicketSearch ?? "")
         .trim()
         .toLowerCase();
      if (!query) return browseTicketRows;

      return browseTicketRows.filter((ticket) => {
         const haystack = [
            ticket.ticketCode,
            ticket.name,
            ticket.id,
            ticket.ticketNo,
         ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
         return haystack.includes(query);
      });
   }, [browseTicketRows, browseTicketSearch]);

   const browseSelectedQuestionIndex = useMemo(() => {
      return browseQuestionRows.findIndex(
         (question) => Number(question.id) === Number(browseSelectedQuestionId),
      );
   }, [browseQuestionRows, browseSelectedQuestionId]);

   const browseSelectedQuestion =
      browseSelectedQuestionIndex >= 0
         ? browseQuestionRows[browseSelectedQuestionIndex]
         : null;

   const browsePreviewText =
      browsePreviewLang === "ru"
         ? browseSelectedQuestion?.textRu || browseSelectedQuestion?.textRo
         : browseSelectedQuestion?.textRo || browseSelectedQuestion?.textRu;

   return (
      <section className="exam-admin-page">
         <div className="exam-admin exam-correction theory-correction">
            {alert ? (
               <div className="exam-admin__pager">
                  <span>
                     <strong>{String(alert.type || "info").toUpperCase()}</strong>:{" "}
                     {alert.message}
                  </span>
                  <button
                     type="button"
                     className="exam-admin__action-btn"
                     onClick={() => setAlert(null)}
                  >
                     Închide
                  </button>
               </div>
            ) : null}

            <div className="exam-admin__topbar">
               <div className="exam-correction__hero">
                  <h2 className="exam-correction__title">Corectare teorie</h2>
                  <p className="exam-admin__muted">
                     Importă <strong>auto_test_AB.json</strong>. Biletele se găsesc
                     după nume (P1–P24, ca la student), întrebările după{" "}
                     <strong>poziția în bilet</strong> — nu după id intern.
                  </p>
               </div>

               <div className="exam-admin__actions">
                  <Link to="/admin/altele" className="exam-admin__action-btn">
                     Instrumente
                  </Link>
                  <Link
                     to="/admin/corectare-exam"
                     className="exam-admin__action-btn"
                  >
                     Corectare exam
                  </Link>
               </div>
            </div>

            <div className="exam-admin__actions theory-correction__browse-actions">
               <button
                  type="button"
                  className="exam-admin__action-btn"
                  onClick={loadBrowseTickets}
                  disabled={browseTicketLoading || browseSelectedTicketLoading}
               >
                  {browseTicketLoading ? "Se încarcă biletele…" : "Reîncarcă biletele"}
               </button>
               <button
                  type="button"
                  className="exam-admin__action-btn"
                  onClick={() =>
                     browseSelectedTicketId &&
                     loadBrowseSelectedTicket(browseSelectedTicketId)
                  }
                  disabled={
                     !browseSelectedTicketId ||
                     browseSelectedTicketLoading ||
                     browseTicketLoading
                  }
               >
                  Reîncarcă biletul curent
               </button>
            </div>

            <div className="exam-admin__layout exam-admin__layout--questions theory-correction__browse-layout">
               <article className="exam-admin__panel">
                  <div className="exam-admin__panel-head">
                     <h3>Bilete teorie (site)</h3>
                     <span className="exam-admin__badge">{browseTicketRows.length}</span>
                  </div>

                  <p className="exam-admin__muted theory-correction__browse-hint">
                     Practice P1–P24 · id DB #{practiceIdRange.start}–#
                     {practiceIdRange.end} (ex. P1=#{practiceIdRange.start}, P24=
                     #{practiceIdRange.end})
                  </p>

                  <div className="exam-admin__toolbar">
                     <input
                        type="text"
                        className="exam-admin__input"
                        placeholder="Caută P1, nume sau id DB"
                        value={browseTicketSearch}
                        onChange={(event) => setBrowseTicketSearch(event.target.value)}
                        disabled={browseTicketLoading}
                     />
                  </div>

                  <div className="exam-admin__ticket-list">
                     {filteredBrowseTickets.length ? (
                        filteredBrowseTickets.map((ticket) => {
                           const isActive =
                              Number(ticket.id) === Number(browseSelectedTicketId);

                           return (
                              <button
                                 key={ticket.localId}
                                 type="button"
                                 className={`exam-admin__ticket-item${
                                    isActive ? " exam-admin__ticket-item--active" : ""
                                 }`}
                                 onClick={() => setBrowseSelectedTicketId(ticket.id)}
                              >
                                 <div className="exam-admin__ticket-top">
                                    <strong>
                                       {ticket.ticketCode || ticket.name}
                                    </strong>
                                    <span className="exam-admin__badge">
                                       {ticket.questionCount || 0} întrebări
                                    </span>
                                 </div>
                                 <p>{ticket.name || "(fără nume)"}</p>
                                 <small>
                                    id DB #{ticket.id}
                                    {ticket.ticketNo
                                       ? ` · poziție bilet ${ticket.ticketNo}`
                                       : ""}
                                 </small>
                              </button>
                           );
                        })
                     ) : (
                        <div className="exam-admin__preview-empty">
                           {browseTicketLoading
                              ? "Se încarcă biletele de teorie…"
                              : "Nu există bilete PRACTICE/THEORY."}
                        </div>
                     )}
                  </div>
               </article>

               <article className="exam-admin__panel">
                  <div className="exam-admin__panel-head">
                     <div>
                        <h3>Întrebări în bilet</h3>
                        <p className="exam-admin__muted">
                           {browseSelectedTicketMeta
                              ? `${browseSelectedTicketMeta.ticketCode || browseSelectedTicketMeta.name} · id DB #${browseSelectedTicketMeta.id}`
                              : "Selectează un bilet"}
                        </p>
                     </div>
                     <span className="exam-admin__badge">
                        {browseQuestionRows.length}
                     </span>
                  </div>

                  {browseSelectedTicketMeta ? (
                     <>
                        {browseSelectedTicketLoading ? (
                           <p className="exam-admin__muted">
                              Se încarcă întrebările…
                           </p>
                        ) : null}

                        <div className="theory-correction__question-list">
                           {browseQuestionRows.length ? (
                              browseQuestionRows.map((question, index) => {
                                 const isActive =
                                    Number(question.id) ===
                                    Number(browseSelectedQuestionId);
                                 const preview = truncateText(
                                    question.textRo || question.textRu,
                                 );

                                 return (
                                    <button
                                       key={`browse-q-${question.id}`}
                                       type="button"
                                       className={`theory-correction__question-item${
                                          isActive
                                             ? " theory-correction__question-item--active"
                                             : ""
                                       }`}
                                       onClick={() =>
                                          setBrowseSelectedQuestionId(question.id)
                                       }
                                    >
                                       <div className="theory-correction__question-item-top">
                                          <strong>
                                             Poz. {question.positionInTicket ?? index + 1}
                                          </strong>
                                          <span className="exam-admin__badge">
                                             corect: {question.correctAnswer || "—"}
                                          </span>
                                       </div>
                                       <p>{preview || "(fără text)"}</p>
                                       <small>id intern #{question.id}</small>
                                    </button>
                                 );
                              })
                           ) : (
                              <div className="exam-admin__preview-empty">
                                 Biletul nu are întrebări încărcate.
                              </div>
                           )}
                        </div>
                     </>
                  ) : (
                     <div className="exam-admin__preview-empty">
                        Selectează un bilet din stânga ca să vezi întrebările în
                        ordinea din bilet.
                     </div>
                  )}
               </article>

               <article className="exam-admin__panel exam-admin__panel--preview">
                  <div className="exam-admin__panel-head">
                     <div>
                        <h3>Detaliu întrebare</h3>
                        <p className="exam-admin__muted">
                           {browseSelectedQuestion
                              ? `Poz. ${browseSelectedQuestion.positionInTicket} · id #${browseSelectedQuestion.id}`
                              : "Alege o întrebare din listă"}
                        </p>
                     </div>
                     <div className="exam-admin__lang-switch">
                        <button
                           type="button"
                           className={`exam-admin__lang-btn${
                              browsePreviewLang === "ro"
                                 ? " exam-admin__lang-btn--active"
                                 : ""
                           }`}
                           onClick={() => setBrowsePreviewLang("ro")}
                        >
                           RO
                        </button>
                        <button
                           type="button"
                           className={`exam-admin__lang-btn${
                              browsePreviewLang === "ru"
                                 ? " exam-admin__lang-btn--active"
                                 : ""
                           }`}
                           onClick={() => setBrowsePreviewLang("ru")}
                        >
                           RU
                        </button>
                     </div>
                  </div>

                  {browseSelectedQuestion ? (
                     <div className="theory-correction__question-detail">
                        <div className="theory-correction__question-frame">
                           <div className="theory-correction__question-line">
                              <span className="theory-correction__lang-tag">
                                 {browsePreviewLang.toUpperCase()}
                              </span>
                              <p>{browsePreviewText || "—"}</p>
                           </div>
                        </div>

                        <ul className="theory-correction__detail-answers">
                           {safeList(browseSelectedQuestion.answers).map(
                              (answer, answerIndex) => {
                                 const isCorrect =
                                    Number(browseSelectedQuestion.correctAnswer) ===
                                    answerIndex + 1;
                                 const text =
                                    browsePreviewLang === "ru"
                                       ? answer.ru || answer.ro
                                       : answer.ro || answer.ru;

                                 return (
                                    <li
                                       key={`browse-answer-${answerIndex}`}
                                       className={
                                          isCorrect
                                             ? "theory-correction__detail-answer--correct"
                                             : ""
                                       }
                                    >
                                       <span className="theory-correction__option-index">
                                          {answerIndex + 1}
                                       </span>
                                       {text || "—"}
                                    </li>
                                 );
                              },
                           )}
                        </ul>

                        {browseSelectedQuestion.image ? (
                           <p className="exam-admin__muted">
                              Imagine: {browseSelectedQuestion.image}
                           </p>
                        ) : null}
                     </div>
                  ) : (
                     <div className="exam-admin__preview-empty">
                        Alege o întrebare ca să vezi textul și răspunsurile.
                     </div>
                  )}
               </article>
            </div>

            <article className="exam-admin__panel">
               <div className="exam-admin__panel-head">
                  <h3>Import bilete teorie</h3>
                  <span className="exam-admin__badge">
                     {importMode === "autoTestAb"
                        ? autoTestAbBundle?.summary?.officialTotal || 0
                        : importMode === "official"
                          ? officialBundle?.summary?.questionsTotal || 0
                          : parsedPatch?.questionPatches?.length || 0}
                  </span>
               </div>

               <div className="theory-correction__import">
                  <div className="theory-correction__import-actions">
                     <label className="exam-admin__primary-btn theory-correction__file-btn">
                        Alege fișier JSON
                        <input
                           type="file"
                           accept="application/json,.json"
                           onChange={handleImportFile}
                           disabled={previewLoading || busy}
                        />
                     </label>
                     {importMode === "autoTestAb" ? (
                        <>
                           <button
                              type="button"
                              className="exam-admin__action-btn"
                              onClick={() =>
                                 autoTestAbBundle &&
                                 runAutoTestAbDryRun(autoTestAbBundle)
                              }
                              disabled={!autoTestAbBundle || busy}
                           >
                              {autoTestAbDryRunLoading
                                 ? "Dry-run…"
                                 : "Re-rulează dry-run"}
                           </button>
                           <button
                              type="button"
                              className="exam-admin__primary-btn"
                              onClick={handleApplyAutoTestAbImport}
                              disabled={
                                 !autoTestAbReport?.updates?.length || busy
                              }
                           >
                              {autoTestAbApplying
                                 ? "Se aplică (APPLY)…"
                                 : `Aplică (${autoTestAbReport?.report?.wouldUpdate || 0})`}
                           </button>
                        </>
                     ) : null}
                     {importMode === "official" ? (
                        <button
                           type="button"
                           className="exam-admin__action-btn"
                           onClick={handleApplyOfficialImport}
                           disabled={!officialBundle?.tickets?.length || busy}
                        >
                           {officialApplying ? "Se aplică…" : "Aplică importul"}
                        </button>
                     ) : null}
                     <button
                        type="button"
                        className="exam-admin__action-btn"
                        onClick={handleExportAllTickets}
                        disabled={previewLoading || busy}
                     >
                        {exportLoading
                           ? "Se exportă JSON…"
                           : `Exportă JSON P1–P24 (#${practiceIdRange.start}–#${practiceIdRange.end})`}
                     </button>
                  </div>

                  <p className="exam-admin__muted">
                     {patchFileName
                        ? `Fișier: ${patchFileName}${
                             importMode === "autoTestAb"
                        ? " · auto_test_AB (potrivire după poziție în bilet)"
                        : importMode === "official"
                                  ? ` · ${patchTypeLabel(officialBundle?.type)}`
                                  : parsedPatch?.type
                                    ? ` · ${patchTypeLabel(parsedPatch.type)}`
                                    : ""
                          }`
                        : "Format: auto_test_AB.json · ticket P1–P24 + ordinea întrebărilor în bilet"}
                  </p>

                  {importMode === "autoTestAb" && autoTestAbBundle?.rule ? (
                     <div className="theory-correction__rule-box">
                        <strong>Reguli import (auto_test_AB)</strong>
                        <p>{autoTestAbBundle.rule}</p>
                     </div>
                  ) : null}

                  {importMode === "autoTestAb" && autoTestAbReport?.report ? (
                     <div className="theory-correction__summary">
                        <span>
                           {autoTestAbReport.report.foundInSite}/
                           {autoTestAbReport.report.officialTotal} găsite
                        </span>
                        <span>
                           {autoTestAbReport.apply
                              ? autoTestAbReport.report.updated
                              : autoTestAbReport.report.wouldUpdate}{" "}
                           de actualizat
                        </span>
                        <span>{autoTestAbReport.report.missingInSite} lipsă site</span>
                        <span>
                           {autoTestAbReport.report.notInOfficialFile} doar site
                        </span>
                        {autoTestAbReport.report.answerCountMismatch > 0 ? (
                           <span className="theory-correction__summary-warn">
                              {autoTestAbReport.report.answerCountMismatch} nr.
                              răspunsuri diferit
                           </span>
                        ) : null}
                     </div>
                  ) : null}

                  {importMode === "official" && officialBundle?.rule ? (
                     <div className="theory-correction__rule-box">
                        <strong>Reguli import</strong>
                        <p>{officialBundle.rule}</p>
                     </div>
                  ) : null}

                  {importMode === "official" && officialBundle?.summary ? (
                     <div className="theory-correction__summary">
                        <span>
                           {officialBundle.summary.ticketsTotal} bilete (
                           {officialBundle.summary.ticketRange || "P1–P24"})
                        </span>
                        <span>{officialBundle.summary.questionsTotal} întrebări</span>
                        {officialBundle.summary.reviewCount > 0 ? (
                           <span className="theory-correction__summary-warn">
                              {officialBundle.summary.reviewCount} de verificat
                           </span>
                        ) : null}
                     </div>
                  ) : null}

                  {importMode === "patch" && parsedPatch?.summary ? (
                     <div className="theory-correction__summary">
                        <span>
                           {parsedPatch.summary.questionsPatched ??
                              parsedPatch.summary.correctionsTotal ??
                              reviewStats.total}{" "}
                           corecții
                        </span>
                        <span>{parsedPatch.summary.ticketsTotal ?? 24} bilete</span>
                        {parsedPatch.summary.lowReviewNeeded != null ? (
                           <span className="theory-correction__summary-warn">
                              {parsedPatch.summary.lowReviewNeeded} de verificat
                           </span>
                        ) : null}
                     </div>
                  ) : null}

                  {autoTestAbApplyProgress ? (
                     <p className="exam-admin__muted">
                        APPLY: {autoTestAbApplyProgress.current}/
                        {autoTestAbApplyProgress.total}
                        {autoTestAbApplyProgress.label
                           ? ` · ${autoTestAbApplyProgress.label}`
                           : ""}
                     </p>
                  ) : null}

                  {importMode === "autoTestAb" && autoTestAbReport?.rows?.length ? (
                     <details className="theory-correction__warnings">
                        <h4>Raport dry-run / apply</h4>
                        <ul>
                           {autoTestAbReport.rows.slice(0, 40).map((row, index) => (
                              <li key={`${row.status}-${row.bKey}-${index}`}>
                                 <strong>{row.status}</strong> · {row.ticketCode}{" "}
                                 {row.bKey}
                                 {row.questionId ? ` · #${row.questionId}` : ""}:{" "}
                                 {row.message}
                              </li>
                           ))}
                           {autoTestAbReport.rows.length > 40 ? (
                              <li>
                                 … și încă {autoTestAbReport.rows.length - 40} linii
                              </li>
                           ) : null}
                        </ul>
                     </details>
                  ) : null}

                  {importMode === "patch" && previewLoading && previewProgress ? (
                     <p className="exam-admin__muted">
                        Se încarcă previzualizarea… {previewProgress.current}/
                        {previewProgress.total}
                     </p>
                  ) : null}

                  {exportProgress ? (
                     <p className="exam-admin__muted">
                        Export JSON: {exportProgress.current}/{exportProgress.total}
                        {exportProgress.ticketName
                           ? ` · ${exportProgress.ticketName}`
                           : ""}
                     </p>
                  ) : null}

                  {officialApplyProgress ? (
                     <p className="exam-admin__muted">
                        Aplicare import: {officialApplyProgress.current}/
                        {officialApplyProgress.total}
                        {officialApplyProgress.label
                           ? ` · ${officialApplyProgress.label}`
                           : ""}
                     </p>
                  ) : null}

                  {importMode === "patch" && previewItems.length ? (
                     <div className="theory-correction__stats">
                        <span>{reviewStats.pending} de decis</span>
                        <span>{reviewStats.applied} schimbate</span>
                        <span>{reviewStats.skipped} păstrate</span>
                        <span>{reviewStats.errors} erori</span>
                     </div>
                  ) : null}

                  {importMode === "patch" && previewItems.length ? (
                     <div className="theory-correction__bulk-bar">
                        <button
                           type="button"
                           className="theory-correction__bulk-btn"
                           onClick={confirmAndApplyAll}
                           disabled={
                              !pendingItems.length || previewLoading || busy
                           }
                        >
                           {bulkApplying
                              ? "Se aplică tot…"
                              : `Confirmă tot (${pendingItems.length})`}
                        </button>
                        {bulkApplying ? (
                           <button
                              type="button"
                              className="exam-admin__danger-btn"
                              onClick={stopRun}
                           >
                              Oprește
                           </button>
                        ) : null}
                     </div>
                  ) : null}

                  {applyProgress ? (
                     <p className="exam-admin__muted">
                        {applyProgress.current}/{applyProgress.total} ·{" "}
                        {applyProgress.label}
                     </p>
                  ) : null}
               </div>

               {importMode === "autoTestAb" && autoTestAbBundle?.errors?.length ? (
                  <div className="theory-correction__warnings">
                     <h4>Avertismente import</h4>
                     <ul>
                        {autoTestAbBundle.errors.slice(0, 12).map((item) => (
                           <li key={item}>{item}</li>
                        ))}
                     </ul>
                  </div>
               ) : null}

               {importMode === "official" && officialBundle?.errors?.length ? (
                  <div className="theory-correction__warnings">
                     <h4>Avertismente import</h4>
                     <ul>
                        {officialBundle.errors.slice(0, 12).map((item) => (
                           <li key={item}>{item}</li>
                        ))}
                     </ul>
                  </div>
               ) : null}

               {importMode === "patch" && parsedPatch?.errors?.length ? (
                  <div className="theory-correction__warnings">
                     <h4>Avertismente import</h4>
                     <ul>
                        {parsedPatch.errors.slice(0, 12).map((item) => (
                           <li key={item}>{item}</li>
                        ))}
                        {parsedPatch.errors.length > 12 ? (
                           <li>… și încă {parsedPatch.errors.length - 12}</li>
                        ) : null}
                     </ul>
                  </div>
               ) : null}
            </article>

            {importMode === "patch" && previewItems.length ? (
               <article className="exam-admin__panel theory-correction__review-panel">
                  <div className="exam-admin__panel-head">
                     <h3>Revizuire întrebări</h3>
                     <span className="exam-admin__badge">
                        {visiblePreviewItems.length
                           ? `${currentIndex + 1}/${visiblePreviewItems.length}`
                           : 0}
                     </span>
                  </div>

                  <div className="theory-correction__ticket-tabs">
                     {ticketTabs.map((code) => (
                        <button
                           key={code}
                           type="button"
                           className={`theory-correction__ticket-tab${
                              selectedTicket === code ? " is-active" : ""
                           }`}
                           onClick={() => setSelectedTicket(code)}
                        >
                           {code === "all" ? "Toate" : code}
                        </button>
                     ))}
                  </div>

                  <div className="theory-correction__review-nav">
                     <button
                        type="button"
                        className="theory-correction__nav-btn"
                        onClick={() => setCurrentIndex((value) => value - 1)}
                        disabled={!canGoPrev || busy}
                     >
                        Înapoi
                     </button>
                     <span className="theory-correction__review-counter">
                        Întrebarea {currentIndex + 1} din {visiblePreviewItems.length}
                     </span>
                     <button
                        type="button"
                        className="theory-correction__nav-btn"
                        onClick={() => setCurrentIndex((value) => value + 1)}
                        disabled={!canGoNext || busy}
                     >
                        Următoarea
                     </button>
                  </div>

                  {currentItem ? (
                     <>
                        <TheoryPreviewCard
                           item={currentItem}
                           decisionStatus={currentDecision}
                        />

                        <div className="theory-correction__decision-actions">
                           <button
                              type="button"
                              className="theory-correction__decision-btn theory-correction__decision-btn--apply"
                              onClick={handleApplyCurrent}
                              disabled={
                                 !currentReviewable ||
                                 busy ||
                                 currentDecision === DECISION.APPLIED
                              }
                           >
                              {itemBusy ? "Se aplică…" : "Schimbăm"}
                           </button>
                           <button
                              type="button"
                              className="theory-correction__decision-btn theory-correction__decision-btn--skip"
                              onClick={handleSkipCurrent}
                              disabled={
                                 busy ||
                                 currentDecision === DECISION.APPLIED ||
                                 currentDecision === DECISION.SKIPPED
                              }
                           >
                              Nu schimbăm
                           </button>
                        </div>
                     </>
                  ) : (
                     <p className="exam-admin__muted theory-correction__empty">
                        Nu există întrebări pentru filtrul selectat.
                     </p>
                  )}
               </article>
            ) : null}

            {applyResults.length ? (
               <article className="exam-admin__panel">
                  <div className="exam-admin__panel-head">
                     <h3>Jurnal aplicare</h3>
                     <span className="exam-admin__badge">{applyResults.length}</span>
                  </div>
                  <ul className="theory-correction__log">
                     {applyResults.slice(0, 30).map((row, index) => (
                        <li
                           key={`${row.target}-${index}`}
                           className={`theory-correction__log-item theory-correction__log-item--${row.status}`}
                        >
                           <strong>{row.target}</strong>
                           <span>{row.message}</span>
                        </li>
                     ))}
                  </ul>
               </article>
            ) : null}
         </div>
      </section>
   );
}
